import React, { useState, useCallback } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import NavigationTopbar from '../../NavigationTopbar'
import Txt from '../../Txt'
import Button from '../../Button'
import useStyles from './useStyles'
import { bidOrder, bidOffer } from '../../../redux/loadDetails/loadDetailsActions'
import ConfirmationDialog from '../../ConfirmationDialog'
import { useTheme } from '../../../theming/ThemeProvider'
import KeyboardView from '../../KeyboardView'
import { usePlatforms } from '../../../hooks/usePlatforms'
import { Formik } from 'formik'
import * as Yup from 'yup'
import InputFormik from '../../InputFormik'
import { loadDetailSelector } from '../../../redux/loadDetails/loadDetailsSelector'

const validationSchema = oldRate => {
  const value = oldRate ? +oldRate : 0
  return Yup.object({
    rate: Yup.number()
      .min(value + 1, 'Rate cannot be less than current rate')
      .required('Rate is required')
  })
}

const BidForm = (props) => {
  const { rate: oldRate, orderNumber, movementNumber, offerHistory, module, onClose } = props
  const dispatch = useDispatch()
  const { loadingBid } = useSelector(loadDetailSelector)
  const { isTablet } = usePlatforms()
  const { theme } = useTheme()
  const styles = useStyles(theme, isTablet)

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(null)

  const handelBid = ({ rate }) => setConfirmationDialogOpen(rate)

  const handleConfirmationDialog = useCallback((action) => {
    const rate = confirmationDialogOpen
    if (action) {
      const payload = {
        order_id: orderNumber || '--',
        movement_id: movementNumber || '--',
        rate: +rate
      }
      if (module === 'all-loads' || module === 'my-loads') {
        dispatch(bidOrder(payload))
      } else {
        dispatch(bidOffer({
          ...payload,
          offer_id: offerHistory[offerHistory.length - 1].offer_id
        }))
      }
    }
    setConfirmationDialogOpen(null)
  }, [dispatch, module, movementNumber, confirmationDialogOpen, offerHistory, orderNumber])

  return (
    <KeyboardView>
      <View style={styles.container}>
        <NavigationTopbar
          onGoBack={onClose}
        />
        <Formik
          validationSchema={validationSchema(oldRate)}
          initialValues={{ rate: '' }}
          onSubmit={handelBid}
        >
          {({ values, setFieldValue, errors, touched, handleSubmit }) => (
            <>
              <View>
                <Txt
                  text='Bid Order'
                  identity='h3'
                />
                <View style={styles.rateContainer}>
                  <Txt
                    text='Current Rate'
                    identity='body2'
                  />
                  <View style={styles.rateBadge}>
                    <Txt
                      text={`${oldRate || '- -'} USD`}
                      identity='body2'
                      color='FERN'
                      selectable
                    />
                  </View>
                </View>
                <InputFormik
                  name='rate'
                  placeholder='New Rate'
                  value={+values?.rate || ''}
                  onChange={setFieldValue}
                  error={touched?.rate && errors?.rate}
                  testID='bidFormRateInput'
                  accessibilityLabel='bidFormRateInput'
                />
              </View>
              <View style={styles.buttonContainer}>
                <Button
                  title='Bid'
                  onPress={handleSubmit}
                  loading={loadingBid}
                  testID='bidFormButton'
                  accessibilityLabel='bidFormButton'
                />
              </View>
            </>
          )}
        </Formik>
      </View>
      <ConfirmationDialog
        open={Boolean(confirmationDialogOpen)}
        title='Bid Offer'
        description='Are sure you want to bid this offer? An Edge dispatcher will be notified immediately.'
        onClose={handleConfirmationDialog}
      />
    </KeyboardView>
  )
}

BidForm.propTypes = {
  rate: PropTypes.string,
  orderNumber: PropTypes.string,
  movementNumber: PropTypes.string,
  offerHistory: PropTypes.array,
  module: PropTypes.string,
  onClose: PropTypes.func
}

export default BidForm
